import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import UserColumn from './userColumn';
import { getAllUserManagement, userPatchApi, userDeleteApi } from './user.service';
import { decideDareApproval, listAllDareApprovals } from '../dareApprovals/dareApprovals.service';
import { REJECTION_REASONS, isEkutirPending } from '../dareApprovals/dareApproval.constants';
import { resultMessage } from '../../../../service/apiResult';
import Loader from '../../../components/loader';
import GenericDataGrid from '../../../components/genericTable';
import { createManagementTableSx } from '../../../styles/tableStyles';
import AlertToast from '../../../components/alertToast';
import InlineAlert from '../../../components/inlineAlert';
import { FilterChips, FilterSearchField, FilterSelect } from '../../../components/filterControls';
import useUrlFilters from '../../../components/useUrlFilters';
import { personaLabel } from '../../../constant/personaLabel';
import { clearFiltersButtonSx, filterBarSx } from '../../../styles/filterStyles';
import { tableApproveButtonSx, tableDeactivateButtonSx, tableRejectButtonSx } from '../../../styles/actionStyles';

const dataGridSx = createManagementTableSx({
  headerHeight: 44,
  headerFontSize: 16,
  cellPaddingX: 12,
  hideSelectedRowCount: false,
  checkboxColor: '#4F8506',
});

const statusActionLabel = {
  ACTIVE: 'approve',
  REJECTED: 'reject',
  INACTIVE: 'deactivate',
  DELETED: 'delete',
};

// Users with no administrative location on file have no country at all — typically head-office
// personas. They need a bucket of their own, otherwise picking any country hides them with no
// obvious way back, and on a 500-row list that reads as rows having gone missing.
const UNSPECIFIED_COUNTRY = '__unspecified__';


// Fresh empty DataGrid (v8) selection model. Always create a new Set so the grid
// treats it as a real change and drops any previously selected (now removed) row ids.
const emptySelection = () => ({ type: 'include', ids: new Set() });

// The status quick filters, in review order: what needs a decision first. DEACTIVATED is shown,
// and counted, as Inactive — the status chip already renders it that way.
const STATUS_ORDER = ['PENDING', 'ACTIVE', 'INACTIVE', 'REJECTED'];
const STATUS_LABEL = { PENDING: 'Pending', ACTIVE: 'Active', INACTIVE: 'Inactive', REJECTED: 'Rejected' };
const statusOf = (user) => {
  const status = String(user.status || '').toUpperCase();
  return status === 'DEACTIVATED' ? 'INACTIVE' : status;
};

// One more quick filter beside the statuses: DAREs whose sign-up only eKutir can approve (no area
// integrator decides them), across any account status — an Active DARE can still be waiting. It is
// the one DARE approval state a reviewer acts on from this screen; the others (waiting for an
// integrator, the decision history) are on each DARE's details page.
const DARE_EKUTIR = 'DARE_EKUTIR';

// The filters this screen keeps in the URL (see useUrlFilters).
const FILTER_KEYS = ['q', 'status', 'country', 'persona'];

const countBy = (rows, keyOf) => {
  const counts = new Map();
  rows.forEach((row) => {
    const key = keyOf(row);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

// Personas left out of the list unless the reviewer searches. Matched against the persona name,
// lower-cased. SOCIETAL_GROUP users are headless accounts backing a dare.societal_groups row
// (created with a group, never log in), so there is nothing to onboard.
const EXCLUDED_PERSONAS = ['farmer', 'agent', 'societal_group'];

export default function User() {
  const navigate = useNavigate();
  // Every user the API returns, hidden personas included; see allFilteredUsers below.
  const [allUsers, setAllUsers] = useState([]);
  const [userData, setUserData] = useState([]);
  const [errors, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Filters live in the URL, so opening a user and coming Back returns to the same filtered list.
  const [filters, setFilters, resetFilters] = useUrlFilters(FILTER_KEYS);
  const { status: statusFilter, country: countryFilter, persona: personaFilter } = filters;
  const search = filters.q.trim().toLowerCase();
  // The list leaves out the hidden personas (EXCLUDED_PERSONAS), but a search looks through every
  // user, so a farmer or agent can still be found by name, email or phone.
  const allFilteredUsers = useMemo(
    () => (search ? allUsers : allUsers.filter((user) => !EXCLUDED_PERSONAS.includes((user.persona || '').toLowerCase()))),
    [allUsers, search]
  );
  // The box itself updates on every keystroke; the URL (and so the filtering) follows after a pause.
  const [searchInput, setSearchInput] = useState(filters.q);
  // EK-02: each DARE's sign-up approval, by user id. DARE Approvals is merged into this screen.
  const [dareApprovals, setDareApprovals] = useState({});
  const [dareRejectReason, setDareRejectReason] = useState('');
  const [currenntPagemain, setCurrentPagemain] = useState(0);
  const [rowsperpagemain, setrowsperpagemain] = useState(10);
  // MUI X DataGrid v8 selection model shape; a fresh empty model is used to clear selection
  // so the grid never holds a stale id after a row is removed (avoids "No row with id ... found").
  const [selectedrows, setSelectedrows] = useState(emptySelection);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState('');
  const [confirmAction, setConfirmAction] = useState({ open: false, status: '', ids: [] });
  // A refused action (e.g. a server-side rule blocking an approval) stays above the table until
  // dismissed. A toast slides away before a refusal that names what to fix can be acted on.
  const [actionError, setActionError] = useState('');

  const selectedIds = selectedrows?.ids instanceof Set ? Array.from(selectedrows.ids) : [];

  const hasSelectedRows = selectedIds.length > 0;
  const selectedRowsData = allFilteredUsers.filter((row) => selectedIds.includes(row.id));
  const selectedApprovableIds = selectedRowsData
    .filter((row) => row.status !== 'ACTIVE' || isEkutirPending(dareApprovals[row.id]))
    .map((row) => row.id);
  const selectedRejectableIds = selectedRowsData.filter((row) => row.status !== 'REJECTED').map((row) => row.id);
  const selectedDeactivatableIds = selectedRowsData
    .filter((row) => row.status !== 'INACTIVE' && row.status !== 'DEACTIVATED')
    .map((row) => row.id);

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const getUserData = useCallback(async () => {
    setLoading(true);

    listAllDareApprovals().then((result) => setDareApprovals(result.success ? result.data : {}));
    const firstRes = await getAllUserManagement(0, 100);
    if (firstRes.statusCode !== 200) {
      setLoading(false);
      setAllUsers([]);
      setError(firstRes.message);
      return;
    }

    const total = firstRes.data.pagination.totalItems;
    const pageCount = Math.ceil(total / 100);
    let fetched = [...firstRes.data.userList];

    if (pageCount > 1) {
      const pageRequests = Array.from({ length: pageCount - 1 }, (_, i) =>
        getAllUserManagement(i + 1, 100)
      );
      const results = await Promise.all(pageRequests);
      for (const res of results) {
        if (res.statusCode === 200) fetched = fetched.concat(res.data.userList);
      }
    }

    setLoading(false);

    // Deliberately does NOT touch the page or the filters: this also runs after an approve,
    // reject, deactivate or delete, and throwing the reviewer back to page 1 of a 500-row list
    // every time they action a row is what EKU-298 is about. Only the rows are refreshed.
    setAllUsers(fetched.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
  }, []);

  useEffect(() => {
    getUserData();
  }, [getUserData]);

  // Debounce the search box so a long name doesn't re-filter (and re-render the grid) per keystroke.
  useEffect(() => {
    if (searchInput === filters.q) return undefined;
    const timer = setTimeout(() => setFilters({ q: searchInput }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters.q, setFilters]);

  // Back/forward or a pasted link can change the query under the box; keep the box in step.
  useEffect(() => {
    setSearchInput((current) => (current.trim() === filters.q.trim() ? current : filters.q));
  }, [filters.q]);

  // One predicate per filter. Each option's count is the number of rows the list would show if that
  // option were picked with every OTHER filter as it is now — so the counts always add up to what
  // the grid shows, and a choice that would empty the table is visible as a 0 before it is clicked.
  const predicates = useMemo(() => {
    const matchesSearch = (user) => {
      if (!search) return true;
      const phone = String(user.phoneNumber || '').trim();
      const dialCode = String(user.countryCode || '').trim();
      const haystack = [
        user.fullName,
        user.email,
        user.persona ? personaLabel(user.persona) : '',
        phone,
        // Also match how the phone is shown in the table ("+91 9876543210") and a pasted
        // number typed without the space, so searching either form finds the user.
        dialCode && phone ? `${dialCode} ${phone}` : '',
        dialCode && phone ? `${dialCode}${phone}` : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    };
    return {
      search: matchesSearch,
      status: (user) => {
        if (!statusFilter) return true;
        if (statusFilter === DARE_EKUTIR) return isEkutirPending(dareApprovals[user.id]);
        return statusOf(user) === statusFilter;
      },
      country: (user) => {
        if (!countryFilter) return true;
        const name = user.countryName || '';
        return countryFilter === UNSPECIFIED_COUNTRY ? !name : name === countryFilter;
      },
      persona: (user) => !personaFilter || user.persona === personaFilter,
    };
  }, [search, statusFilter, countryFilter, personaFilter, dareApprovals]);

  const rowsExcept = useCallback(
    (skip) => allFilteredUsers.filter((user) =>
      Object.entries(predicates).every(([name, matches]) => name === skip || matches(user))),
    [allFilteredUsers, predicates]
  );

  const statusOptions = useMemo(() => {
    const scope = rowsExcept('status');
    const counts = countBy(scope, statusOf);
    const waitingForEkutir = scope.filter((user) => isEkutirPending(dareApprovals[user.id])).length;
    return [
      { value: '', label: 'All', count: scope.length },
      ...STATUS_ORDER
        .filter((status) => counts.get(status) || status === statusFilter)
        .map((status) => ({ value: status, label: STATUS_LABEL[status], count: counts.get(status) || 0 })),
      ...(waitingForEkutir || statusFilter === DARE_EKUTIR
        ? [{ value: DARE_EKUTIR, label: 'DARE waiting for eKutir', count: waitingForEkutir }]
        : []),
    ];
  }, [rowsExcept, statusFilter, dareApprovals]);

  // Countries and personas come from the rows themselves: persona is a free-form name from the DB,
  // so a hardcoded list would offer choices that never match anything. Options that would leave
  // nothing are dropped, except the one currently picked, which stays visible (with its 0).
  const countryOptions = useMemo(() => {
    const counts = countBy(rowsExcept('country'), (user) => user.countryName || UNSPECIFIED_COUNTRY);
    if (countryFilter && !counts.has(countryFilter)) counts.set(countryFilter, 0);
    return Array.from(counts.entries())
      .sort(([a], [b]) => (a === UNSPECIFIED_COUNTRY ? 1 : b === UNSPECIFIED_COUNTRY ? -1 : a.localeCompare(b)))
      .map(([value, count]) => ({ value, label: value === UNSPECIFIED_COUNTRY ? 'Not specified' : value, count }));
  }, [rowsExcept, countryFilter]);

  const personaOptions = useMemo(() => {
    const counts = countBy(rowsExcept('persona').filter((user) => user.persona), (user) => user.persona);
    if (personaFilter && !counts.has(personaFilter)) counts.set(personaFilter, 0);
    return Array.from(counts.entries())
      .sort(([a], [b]) => personaLabel(a).localeCompare(personaLabel(b)))
      .map(([value, count]) => ({ value, label: personaLabel(value), count }));
  }, [rowsExcept, personaFilter]);

  const visibleUsers = useMemo(
    // Newest first is the only ordering applied here; per-column ordering is the grid's own
    // header sort, which the reviewer drives from the column titles.
    () => [...rowsExcept(null)].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [rowsExcept]
  );

  // Narrowing the list would otherwise leave the user on a page that no longer exists, and leave
  // rows selected that they can no longer see — so the bulk buttons would act on invisible users.
  useEffect(() => {
    setCurrentPagemain(0);
    setSelectedrows(emptySelection());
  }, [search, statusFilter, personaFilter, countryFilter]);

  // Staying put must not mean staying on a page that no longer exists — deleting the last row of
  // the last page, or a status change that filters a row out, can shrink the list past the current
  // page. Fall back to the nearest page that still has rows, not to the first one.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(visibleUsers.length / rowsperpagemain) - 1);
    if (currenntPagemain > lastPage) setCurrentPagemain(lastPage);
  }, [visibleUsers.length, rowsperpagemain, currenntPagemain]);

  useEffect(() => {
    const start = currenntPagemain * rowsperpagemain;
    setUserData(visibleUsers.slice(start, start + rowsperpagemain));
  }, [visibleUsers, currenntPagemain, rowsperpagemain]);

  const hasActiveFilters = Boolean(search || statusFilter || personaFilter || countryFilter);

  const clearFilters = () => {
    setSearchInput('');
    resetFilters();
  };

  const requestApprovalAction = (status, ids = []) => {
    const normalizedIds =
      ids?.ids instanceof Set ? Array.from(ids.ids) : Array.isArray(ids) ? ids : [];

    if (normalizedIds.length === 0) return;

    setDareRejectReason('');
    setConfirmAction({
      open: true,
      status,
      ids: normalizedIds,
    });
  };

  const closeConfirmAction = () => {
    setConfirmAction({ open: false, status: '', ids: [] });
  };

  const handleApprovalStatus = async () => {
    if (!confirmAction.ids.length || !confirmAction.status) return;

    if (confirmAction.status === 'DELETED') {
      try {
        setLoading(true);
        const results = await Promise.all(confirmAction.ids.map((id) => userDeleteApi(id)));
        setLoading(false);

        const failed = results.find((r) => !r.success);
        closeConfirmAction();
        if (failed) {
          setActionError(failed.message || 'Failed to delete user(s)');
        } else {
          setActionError('');
          setResponseMsg('User(s) deleted successfully');
          setAlert('true');
          setOpenAlert(true);
        }
        if (!failed) {
          // Clear the selection before the rows refresh so it can't reference the removed row.
          //
          // The grid is deliberately NOT remounted here (B-11): a remount rebuilds its pagination,
          // sort and scroll state from scratch, and delete — the only action that remounted it —
          // was the one that threw the reviewer back to page 1, while approve, reject and
          // deactivate kept their place (EKU-298). The grid already moves its focus off a row that
          // disappears from `rows`, and the confirm dialog doesn't hand focus back to the removed
          // row (disableRestoreFocus), which is what the remount guarded against
          // (MissingRowIdError). If the deleted row was the only one on the last page, the page
          // clamp above steps back to the previous page.
          setSelectedrows(emptySelection());
          getUserData();
        }
      } catch (error) {
        console.error('An error occurred:', error);
        setLoading(false);
        setActionError('Something went wrong. Please try again.');
        closeConfirmAction();
      }
      return;
    }

    const data = {
      userIds: confirmAction.ids,
      approvalStatus: confirmAction.status,
    };

    try {
      setLoading(true);
      const response = await userPatchApi(data);
      setLoading(false);

      if (response.success) {
        const dareFailure = await decideDareSignups(confirmAction.status, confirmAction.ids);
        setActionError(dareFailure);
        setResponseMsg(resultMessage(response, 'User(s) updated successfully'));
        setAlert('true');
        setOpenAlert(true);
        closeConfirmAction();
        setSelectedrows(emptySelection());
        getUserData();
      } else {
        // A refusal explains what has to change first, so it stays above the table until
        // dismissed instead of being flashed in a toast. Nothing changed — nothing to refresh.
        setActionError(resultMessage(response));
        closeConfirmAction();
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setLoading(false);
      setActionError('Something went wrong. Please try again.');
      closeConfirmAction();
    }
  };

  // DAREs in the selection whose pending sign-up eKutir decides (the rest belong to an integrator).
  const ekutirDareIds = (ids) => ids.filter((id) => isEkutirPending(dareApprovals[id]));
  const confirmDareIds =
    confirmAction.status === 'ACTIVE' || confirmAction.status === 'REJECTED' ? ekutirDareIds(confirmAction.ids) : [];

  /** Approving or rejecting the account also decides the DARE sign-ups eKutir owns; returns an error or ''. */
  async function decideDareSignups(status, ids) {
    const dareIds = status === 'ACTIVE' || status === 'REJECTED' ? ekutirDareIds(ids) : [];
    if (!dareIds.length) return '';
    const body = status === 'ACTIVE' ? { status: 'APPROVED' } : { status: 'REJECTED', reasonCode: dareRejectReason };
    const results = await Promise.all(dareIds.map((id) => decideDareApproval(id, body)));
    const failed = results.find((result) => !result.success);
    return failed ? `The account was updated, but a DARE sign-up decision failed: ${resultMessage(failed)}` : '';
  }

  const userColumnData = useMemo(
    () =>
      UserColumn({
        navigate,
        requestApprovalAction,
        dareApprovals,
      }),
    [navigate, dareApprovals]
  );

  return (
    <>
      <Grid container mb={4} alignItems="center" rowSpacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography
            className="app-page-title"
            sx={{ fontSize: '34px', lineHeight: '40px', letterSpacing: '-0.25px', fontWeight: 700, color: '#1C1B1F', mb: 1 }}
          >
            User Onboarding
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
            Review user onboarding requests, inspect submitted details, and approve or reject pending access.
            DARE sign-up approvals and billing are on each DARE's details page.
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap', gap: 1.5, mt: { xs: 2, md: 0 } }}>
            <Button
              variant="contained"
              disabled={selectedRejectableIds.length === 0}
              onClick={() => requestApprovalAction('REJECTED', selectedRejectableIds)}
              sx={{ ...tableRejectButtonSx, minWidth: { xs: 'calc(50% - 6px)', sm: 116 }, flexGrow: { xs: 1, sm: 0 }, height: 44, boxShadow: 'none', '&:hover': { bgcolor: '#FDE0DC', boxShadow: 'none' } }}
            >
              Reject
            </Button>

            <Button
              variant="contained"
              disabled={selectedApprovableIds.length === 0}
              onClick={() => requestApprovalAction('ACTIVE', selectedApprovableIds)}
              sx={{ ...tableApproveButtonSx, minWidth: { xs: 'calc(50% - 6px)', sm: 116 }, flexGrow: { xs: 1, sm: 0 }, height: 44, bgcolor: '#4F8506', color: '#FFFFFF', boxShadow: 'none', '&:hover': { bgcolor: '#4F8506', boxShadow: 'none' }, '&.Mui-disabled': { bgcolor: '#E6E6E6', color: '#A0A0A0' } }}
            >
              Approve
            </Button>

            <Button
              variant="contained"
              disabled={selectedDeactivatableIds.length === 0}
              onClick={() => requestApprovalAction('INACTIVE', selectedDeactivatableIds)}
              sx={{ ...tableDeactivateButtonSx, minWidth: { xs: '100%', sm: 132 }, flexGrow: { xs: 1, sm: 0 }, height: 44, boxShadow: 'none', '&:hover': { bgcolor: '#F1F1F1', boxShadow: 'none' } }}
            >
              Deactivate
            </Button>
          </Box>
          {hasSelectedRows && selectedApprovableIds.length === 0 && selectedRejectableIds.length === 0 && selectedDeactivatableIds.length === 0 && (
            <Typography sx={{ mt: 1, textAlign: { xs: 'left', md: 'right' }, fontSize: 12, lineHeight: '16px', letterSpacing: '0.4px', color: '#6C6A86' }}>
              The selected users are already in those final states.
            </Typography>
          )}
        </Grid>
      </Grid>

      <FilterChips
        options={statusOptions}
        value={statusFilter}
        onChange={(value) => setFilters({ status: value })}
        ariaLabel="Filter by status"
        testId="user-status-filter"
      />

      <Box sx={filterBarSx}>
        <FilterSearchField
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search by name, email or phone number"
          testId="user-search"
        />

        <FilterSelect
          value={countryFilter}
          onChange={(value) => setFilters({ country: value })}
          placeholder="All countries"
          options={countryOptions}
          testId="user-country-filter"
        />

        <FilterSelect
          value={personaFilter}
          onChange={(value) => setFilters({ persona: value })}
          placeholder="All personas"
          options={personaOptions}
          testId="user-persona-filter"
        />

        {hasActiveFilters && (
          <Button
            onClick={clearFilters}
            startIcon={<Icon icon="material-symbols:filter-alt-off-outline-rounded" width="18" height="18" />}
            sx={clearFiltersButtonSx}
          >
            Clear filters
          </Button>
        )}
      </Box>

      <InlineAlert message={actionError} onClose={() => setActionError('')} variant="error" />

      <Typography sx={{ mb: 1.5, fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
        {hasActiveFilters
          ? `Showing ${visibleUsers.length} of ${allFilteredUsers.length} user${allFilteredUsers.length === 1 ? '' : 's'}`
          : `${allFilteredUsers.length} user${allFilteredUsers.length === 1 ? '' : 's'}`}
      </Typography>

      <Box sx={{ width: '100%' }}>
        <GenericDataGrid
          rows={userData}
          columns={userColumnData}
          rowCount={visibleUsers.length}
          page={currenntPagemain}
          pageSize={rowsperpagemain}
          loading={loading}
          errors={errors || (hasActiveFilters && visibleUsers.length === 0 ? 'No users match your search or filters' : '')}
          checkbox="true"
          dataGridSx={dataGridSx}
          autoHeight
          rowHeight={60}
          rowSelectionModel={selectedrows}
          onPaginationChange={(page, pageSize) => {
            setCurrentPagemain(page);
            setrowsperpagemain(pageSize);
          }}
          onSelectionChange={(newSelection) => setSelectedrows(newSelection)}
        />
      </Box>

      <Dialog
        open={confirmAction.open}
        onClose={closeConfirmAction}
        // Don't restore focus to the triggering row button on close: after a delete that row is
        // gone, and refocusing it makes the grid look up the removed id (MissingRowIdError).
        disableRestoreFocus
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 420,
            borderRadius: '24px',
            p: 1,
            mx: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontSize: 24, lineHeight: '32px', letterSpacing: 0, fontWeight: 700, color: '#1C1B1F' }}>
          Confirm {statusActionLabel[confirmAction.status] || 'update'}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
            {confirmAction.status === 'ACTIVE'
              ? 'Approve the selected user(s)?'
              : confirmAction.status === 'REJECTED'
                ? 'Reject the selected user(s)?'
                : confirmAction.status === 'DELETED'
                  ? 'Delete the selected user(s)?'
                  : 'Deactivate the selected user(s)?'}
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
            {confirmAction.status === 'DELETED'
              ? `This will remove ${confirmAction.ids.length} user${confirmAction.ids.length === 1 ? '' : 's'} from the list. This action cannot be undone.`
              : `This action will update ${confirmAction.ids.length} user${confirmAction.ids.length === 1 ? '' : 's'}.`}
          </Typography>
          {confirmDareIds.length > 0 ? (
            <Typography sx={{ mt: 1.5, fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
              {`${confirmDareIds.length} DARE sign-up request${confirmDareIds.length === 1 ? '' : 's'} eKutir decides will also be ${confirmAction.status === 'ACTIVE' ? 'approved' : 'rejected'}.`}
            </Typography>
          ) : null}
          {confirmAction.status === 'REJECTED' && confirmDareIds.length > 0 ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Reason shown to the DARE"
              value={dareRejectReason}
              onChange={(event) => setDareRejectReason(event.target.value)}
              sx={{ mt: 2 }}
            >
              {REJECTION_REASONS.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>{reason.label}</MenuItem>
              ))}
            </TextField>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, justifyContent: 'flex-end', gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: 'stretch' }}>
          <Button
            onClick={closeConfirmAction}
            sx={{
              minWidth: 88,
              height: 40,
              borderRadius: '10px',
              textTransform: 'none',
              color: '#DA342E',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprovalStatus}
            disabled={confirmAction.status === 'REJECTED' && confirmDareIds.length > 0 && !dareRejectReason}
            variant="contained"
            sx={{
              minWidth: 100,
              height: 40,
              borderRadius: '10px',
              textTransform: 'none',
              bgcolor:
                confirmAction.status === 'ACTIVE'
                  ? '#4F8506'
                  : confirmAction.status === 'REJECTED' || confirmAction.status === 'DELETED'
                    ? '#DA342E'
                    : '#6C6A86',
              color: '#FFFFFF',
              boxShadow: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                bgcolor:
                  confirmAction.status === 'ACTIVE'
                    ? '#4F8506'
                    : confirmAction.status === 'REJECTED'
                      ? '#DA342E'
                      : '#6C6A86',
                boxShadow: 'none',
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />

      {loading && <Loader />}
    </>
  );
}
